// Voicing PINS — the user's own grip for one chord of one tune, laid over the
// generated line (user ruling, Sep 2026: "you start automatically with the
// automatically generated ones, and they're like the default"; then "click on
// a chord diagram and edit that… select from a whole chord library of similar
// chord voicings"). A pin is ARRANGEMENT, never the tune: the written chords
// are untouched, the engine fills every unpinned chord and voice-leads into
// and out of each pin (the same anchor the locked opening chord has always
// been), and Reset returns a chord to the engine.
//
// Pins live in LANES, because a grip belongs to a plan × an instrument. The
// comping lanes voice the written changes (one event per chord, the chart+
// comping sheet and the score+comping overlay draw the same line); the melody
// lanes voice the chord-melody plan, whose grips must carry the written note
// on top — so a grip that is right in one lane can be wrong in the other, and
// a pin never crosses lanes. A pin does not cross instruments either: guitar
// pins stay put while the sheet draws keyboards and return with the fretboards.
//
// KEYS are the chord's WRITTEN POSITION — bar index and onset beat, plus the
// melody note's onset under every-note density — not its id: an iReal chart is
// re-parsed on every load with fresh random ids, so an id-keyed pin on a
// library tune would not survive a reload, while a position survives for every
// source. Repeated bars share their written position, so a pin applies on
// every pass. The pin also records the chord TYPE it was made for: a
// reharmonised bar whose quality changed drops the pin rather than playing a
// maj7 grip under a m7♭5. A TRANSPOSED tune keeps its pins — a guitar pin is
// regenerated from the chord (family, inversion, string set), a piano pin's
// pitches move with the root by the nearest way, the same rule `transposeChart`
// applies to the melody.

import type { ChordTypeId } from './chords';
import { pitchClassOf } from './notes';
import { STRING_LAYOUTS, type StringLayoutId, type StringSet } from './tuning';
import type { Fingering, Inversion } from './voicing';
import {
  isVoicingFamily,
  type VoicingFamily,
  type VoicingFamilySelection,
} from './voicingFamilies';

/** Which generated line a pin belongs to: a plan × an instrument. */
export type PinLane = 'comping.guitar' | 'comping.piano' | 'melody.guitar' | 'melody.piano';

export const PIN_LANES: readonly PinLane[] = [
  'comping.guitar',
  'comping.piano',
  'melody.guitar',
  'melody.piano',
];

export type PinPlan = 'comping' | 'melody';
export type PinInstrument = 'guitar' | 'piano';

export function pinLane(plan: PinPlan, instrument: PinInstrument): PinLane {
  return `${plan}.${instrument}`;
}

/** A guitar pin names the grip the way the engine enumerates it, so the grip
 * is REGENERATED from the chord each time — which is what makes a transposed
 * tune keep its pins. `octaveShift` picks the ±12-fret variant the melody
 * plan admits (`allowOctaveShifts`); absent = the base position. */
export interface GuitarEnumeratedPin {
  stringSet: StringSet;
  voicingFamily: VoicingFamily;
  inversion: Inversion;
  octaveShift?: -12 | 0 | 12;
}

/** A grip the user PLACED or took from the library (a V-System spread): the
 * frets themselves on one of the four string layouts, recorded with the root
 * they were placed under so a transposition slides them (`shiftFrets`). */
export interface GuitarFretsPin {
  root: string;
  frets: (number | null)[];
  stringLayout: StringLayoutId;
  /** Snapshot only: deleting a library entry never changes an authored pin. */
  alternativeShape?: Fingering['alternativeShape'];
}

export type GuitarGripPin = GuitarEnumeratedPin | GuitarFretsPin;

export function isFretsPin(pin: GuitarGripPin): pin is GuitarFretsPin {
  return 'frets' in pin;
}

/** A piano pin is the sounding stack itself, recorded against the root it
 * was pinned under so a transposition can move it. */
export interface PianoVoicingPin {
  /** Root pitch-class name (any spelling) at pin time. */
  root: string;
  midis: number[];
  hands?: { left: number[]; right: number[] };
  /** The family and rotation the walk had chosen, for the readout. */
  family?: string;
  inversion?: number;
}

export type VoicingPin =
  | ({ kind: 'guitar'; chordType: ChordTypeId } & GuitarEnumeratedPin)
  | ({ kind: 'guitar-frets'; chordType: ChordTypeId } & GuitarFretsPin)
  | ({ kind: 'piano'; chordType: ChordTypeId } & PianoVoicingPin);

/** One lane's pins by position key. */
export type LanePins = Record<string, VoicingPin>;
export type VoicingPins = Partial<Record<PinLane, LanePins>>;

/** The voicing settings a tune remembers — the Chords menu's rows. */
export interface TuneVoicingSettings {
  voicingFamily: VoicingFamilySelection;
  stringSet: StringSet;
  freeStringSet: boolean;
  startingInversion: number;
  voiceLeadingEngine: string;
}

/** Everything a tune remembers about how YOU voice it (the band and its Feel
 * grooves have their own records beside this one). The shape of the future
 * `TuneArrangementDoc`'s voicing half. */
export interface TuneArrangement {
  voicing?: TuneVoicingSettings;
  pins?: VoicingPins;
}

/**
 * The position key of a chord: the WRITTEN bar's index in the chart and the
 * chord's onset beat inside it (1-based, the chart's own convention). Under
 * every-note density the melody note's onset beat is appended, so a chord that
 * carries four notes has four keys and a pin on one leaves the others to the
 * engine. Repeated passes of a bar share the key, so a pin applies on each.
 */
export function pinKey(measureIndex: number, chordBeat: number, noteBeat?: number): string {
  const base = `${measureIndex}@${fmt(chordBeat)}`;
  return noteBeat == null ? base : `${base}.n${fmt(noteBeat)}`;
}

function fmt(beat: number): string {
  // Beats are quarters or their binary/triplet fractions; four decimals keep
  // a triplet onset exact enough to be its own key and never a float tail.
  return String(Math.round(beat * 10000) / 10000);
}

export function pinCount(pins: VoicingPins | undefined, lane?: PinLane): number {
  if (!pins) return 0;
  const lanes = lane ? [lane] : PIN_LANES;
  return lanes.reduce((n, l) => n + Object.keys(pins[l] ?? {}).length, 0);
}

export function withPin(
  pins: VoicingPins | undefined,
  lane: PinLane,
  key: string,
  pin: VoicingPin,
): VoicingPins {
  return { ...(pins ?? {}), [lane]: { ...(pins?.[lane] ?? {}), [key]: pin } };
}

/** Remove one pin; a lane left empty is dropped so `pinCount` and the
 * persisted blob stay honest. Returns the same object when nothing changes. */
export function withoutPin(pins: VoicingPins | undefined, lane: PinLane, key: string): VoicingPins {
  const lanePins = pins?.[lane];
  if (!pins || !lanePins || !(key in lanePins)) return pins ?? {};
  const rest = { ...lanePins };
  delete rest[key];
  const next: VoicingPins = { ...pins };
  if (Object.keys(rest).length) next[lane] = rest;
  else delete next[lane];
  return next;
}

/** Clear one lane, or every lane. */
export function withoutLane(pins: VoicingPins | undefined, lane?: PinLane): VoicingPins {
  if (!pins) return {};
  if (!lane) return {};
  if (!(lane in pins)) return pins;
  const next = { ...pins };
  delete next[lane];
  return next;
}

/**
 * The pin that applies to a chord at `key`, or undefined: a pin recorded for
 * another chord TYPE (the bar was reharmonised under it) does not apply.
 */
export function pinFor(
  pins: VoicingPins | undefined,
  lane: PinLane,
  key: string | undefined,
  chordType: ChordTypeId,
): VoicingPin | undefined {
  if (!pins || key == null) return undefined;
  const pin = pins[lane]?.[key];
  return pin && pin.chordType === chordType ? pin : undefined;
}

/** Nearest-way semitone shift from one root to another (+11 ≡ −1), the
 * `transposeChart` rule, so a transposed tune's piano pins move with it. */
export function rootShift(fromRoot: string, toRoot: string): number {
  const delta = (((pitchClassOf(toRoot) - pitchClassOf(fromRoot)) % 12) + 12) % 12;
  return delta > 6 ? delta - 12 : delta;
}

/** A piano pin's stack under the chord's CURRENT root. */
export function pianoPinMidis(
  pin: PianoVoicingPin,
  root: string,
): { midis: number[]; hands?: { left: number[]; right: number[] } } {
  const shift = rootShift(pin.root, root);
  if (shift === 0) return { midis: pin.midis, hands: pin.hands };
  return {
    midis: pin.midis.map((m) => m + shift),
    hands: pin.hands
      ? { left: pin.hands.left.map((m) => m + shift), right: pin.hands.right.map((m) => m + shift) }
      : undefined,
  };
}

/** The frets a placed grip takes under the chord's CURRENT root: slid by the
 * nearest-way shift, then folded by an octave when the slide runs off the
 * neck (below the nut or past the 20th fret); null when neither fold fits. */
export function shiftFrets(
  frets: readonly (number | null)[],
  semitones: number,
  maxFret = 20,
): (number | null)[] | null {
  const fits = (list: (number | null)[]) =>
    list.every((f) => f == null || (f >= 0 && f <= maxFret));
  for (const shift of [semitones, semitones + 12, semitones - 12]) {
    const moved = frets.map((f) => (f == null ? null : f + shift));
    if (fits(moved)) return moved;
  }
  return null;
}

/** Rehydrate guard: a persisted blob is untrusted input. Keeps only lanes we
 * know, keys that are strings and pins of a recognised kind with the fields a
 * consumer will read; anything else is dropped, never repaired. */
export function normalizeVoicingPins(raw: unknown): VoicingPins | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const out: VoicingPins = {};
  for (const lane of PIN_LANES) {
    const lanePins = (raw as Record<string, unknown>)[lane];
    if (!lanePins || typeof lanePins !== 'object') continue;
    const clean: LanePins = {};
    for (const [key, pin] of Object.entries(lanePins as Record<string, unknown>)) {
      const p = normalizePin(pin);
      if (p) clean[key] = p;
    }
    if (Object.keys(clean).length) out[lane] = clean;
  }
  return Object.keys(out).length ? out : undefined;
}

function normalizePin(raw: unknown): VoicingPin | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const p = raw as Record<string, unknown>;
  if (typeof p.chordType !== 'string') return undefined;
  const chordType = p.chordType as ChordTypeId;
  if (p.kind === 'guitar') {
    if (
      (p.stringSet !== 'middle' && p.stringSet !== 'upper') ||
      // A physical family the engine can enumerate: an unknown id reaches
      // the family registry as undefined and throws inside the optimiser.
      !isVoicingFamily(p.voicingFamily) ||
      // Catalogue grips are identified by full frets, never by a non-unique
      // four-rotation index. Their persisted kind is guitar-frets.
      p.voicingFamily === 'joe-pass' ||
      ![0, 1, 2, 3].includes(p.inversion as number)
    ) {
      return undefined;
    }
    const octaveShift = [-12, 0, 12].includes(p.octaveShift as number)
      ? (p.octaveShift as -12 | 0 | 12)
      : undefined;
    return {
      kind: 'guitar',
      chordType,
      stringSet: p.stringSet,
      voicingFamily: p.voicingFamily as VoicingFamily,
      inversion: p.inversion as Inversion,
      ...(octaveShift !== undefined ? { octaveShift } : {}),
    };
  }
  if (p.kind === 'guitar-frets') {
    if (
      typeof p.root !== 'string' ||
      !(typeof p.stringLayout === 'string' && p.stringLayout in STRING_LAYOUTS) ||
      !Array.isArray(p.frets) ||
      p.frets.length !== 6 ||
      !p.frets.every((f) => f === null || (Number.isInteger(f) && (f as number) >= 0))
    ) {
      return undefined;
    }
    return {
      kind: 'guitar-frets',
      chordType,
      root: p.root,
      frets: p.frets as (number | null)[],
      stringLayout: p.stringLayout as StringLayoutId,
      ...normalizeShapeProvenance(p.alternativeShape),
    };
  }
  if (p.kind === 'piano') {
    if (typeof p.root !== 'string' || !isMidiList(p.midis) || p.midis.length === 0) {
      return undefined;
    }
    const hands =
      p.hands &&
      typeof p.hands === 'object' &&
      isMidiList((p.hands as Record<string, unknown>).left) &&
      isMidiList((p.hands as Record<string, unknown>).right)
        ? (p.hands as { left: number[]; right: number[] })
        : undefined;
    return {
      kind: 'piano',
      chordType,
      root: p.root,
      midis: p.midis,
      ...(hands ? { hands } : {}),
      ...(typeof p.family === 'string' ? { family: p.family } : {}),
      ...(Number.isInteger(p.inversion) ? { inversion: p.inversion as number } : {}),
    };
  }
  return undefined;
}

/** Metadata is optional: malformed provenance cannot discard exact pinned frets. */
function normalizeShapeProvenance(raw: unknown): Pick<GuitarFretsPin, 'alternativeShape'> {
  if (!raw || typeof raw !== 'object') return {};
  const p = raw as Record<string, unknown>;
  if (
    typeof p.id !== 'string' ||
    !p.id ||
    p.id.length > 120 ||
    typeof p.name !== 'string' ||
    !p.name.trim() ||
    p.name.length > 100
  )
    return {};
  return {
    alternativeShape: {
      id: p.id,
      name: p.name,
      ...(p.construction === 'drop-2' || p.construction === 'drop-3'
        ? { construction: p.construction }
        : {}),
    },
  };
}

function isMidiList(v: unknown): v is number[] {
  return Array.isArray(v) && v.every((m) => Number.isInteger(m));
}

/** The key function for one chart: written bar ID → its index. A bar the
 * chart does not hold (a synthesised or foreign id) has no key, so nothing
 * can pin to it. */
export function pinKeyResolver(
  measureIds: readonly string[],
): (barId: string | undefined, chordBeat: number, noteBeat?: number) => string | undefined {
  const index = new Map(measureIds.map((id, i) => [id, i]));
  return (barId, chordBeat, noteBeat) => {
    if (barId == null) return undefined;
    const i = index.get(barId);
    return i == null ? undefined : pinKey(i, chordBeat, noteBeat);
  };
}

/** Stamp a comping-lane sequence with its keys and the guitar pins that
 * apply — what the optimiser reads (`pin`) and the sheet's marks read
 * (`pinKey`, and `pin` present = pinned). */
export function stampCompingPins<
  T extends { barId?: string; beat: number; chordType: ChordTypeId },
>(
  sequence: T[],
  pins: VoicingPins | undefined,
  keyOf: ReturnType<typeof pinKeyResolver>,
): (T & { pinKey?: string; pin?: GuitarGripPin })[] {
  return sequence.map((chord) => {
    const key = keyOf(chord.barId, chord.beat);
    const pin = pinFor(pins, 'comping.guitar', key, chord.chordType);
    return {
      ...chord,
      ...(key ? { pinKey: key } : {}),
      ...(pin && isGuitarPin(pin) ? { pin: guitarPinOf(pin) } : {}),
    };
  });
}

export function isGuitarPin(
  pin: VoicingPin,
): pin is VoicingPin & { kind: 'guitar' | 'guitar-frets' } {
  return pin.kind === 'guitar' || pin.kind === 'guitar-frets';
}

/** The engine's view of a guitar pin (the record's `kind`/`chordType` are
 * the map's, not the grip's). */
export function guitarPinOf(pin: VoicingPin & { kind: 'guitar' | 'guitar-frets' }): GuitarGripPin {
  if (pin.kind === 'guitar-frets') {
    return {
      root: pin.root,
      frets: pin.frets,
      stringLayout: pin.stringLayout,
      ...(pin.alternativeShape ? { alternativeShape: pin.alternativeShape } : {}),
    };
  }
  return {
    stringSet: pin.stringSet,
    voicingFamily: pin.voicingFamily,
    inversion: pin.inversion,
    ...(pin.octaveShift ? { octaveShift: pin.octaveShift } : {}),
  };
}

/** The piano walk's `pinnedVoicing` for a chord, moved to its current root. */
export function pianoPinnedVoicing(
  pins: VoicingPins | undefined,
  lane: PinLane,
  chord: { pinKey?: string; chordType: ChordTypeId; root: string },
): (PianoVoicingPin & { midis: number[] }) | undefined {
  const pin = pinFor(pins, lane, chord.pinKey, chord.chordType);
  if (!pin || pin.kind !== 'piano') return undefined;
  const moved = pianoPinMidis(pin, chord.root);
  return {
    root: chord.root,
    midis: moved.midis,
    ...(moved.hands ? { hands: moved.hands } : {}),
    ...(pin.family ? { family: pin.family } : {}),
    ...(pin.inversion != null ? { inversion: pin.inversion } : {}),
  };
}
